import React, { useState } from 'react';
import { Card, Form, Button, Alert, ProgressBar, Table } from 'react-bootstrap';
import { FaUpload, FaFileAlt, FaCheck, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { clientApi } from '../../services/api';
import { CLIENT_STATUSES } from '../../utils/clientModel';
import Papa from 'papaparse';

const BulkUpload = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    success: 0,
    failed: 0
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setPreview([]);
    setErrors([]);
    setSuccess(false);
    
    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const csvText = event.target.result;
        const data = parseCSV(csvText);
        setPreview(data);
      };
      reader.readAsText(selectedFile);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = parseCSVLine(lines[0]);
    
    return lines.slice(1).filter(line => line.trim() !== '').map(line => {
      const values = parseCSVLine(line);
      const obj = {};
      
      headers.forEach((header, i) => {
        obj[header.trim()] = values[i] ? values[i].trim() : '';
      });
      
      return obj;
    });
  };
  
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        // Toggle quote state
        inQuotes = !inQuotes;
        // If we're closing quotes and the next char is also a quote, it's an escaped quote
        if (!inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // Skip the next quote
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add the last field
    result.push(current);
    return result;
  };

  const validateData = (data) => {
    const validationErrors = [];
    const requiredFields = ['firstName', 'mobile'];
    
    data.forEach((row, index) => {
      // Check required fields
      for (const field of requiredFields) {
        if (!row[field] || row[field].trim() === '') {
          validationErrors.push(`Row ${index + 1}: Missing required field "${field}"`);
        }
      }
      
      // Validate mobile number format
      if (row.mobile && !/^\d{10}$/.test(row.mobile.replace(/\D/g, ''))) {
        validationErrors.push(`Row ${index + 1}: Invalid mobile number format`);
      }
      
      // Validate status if provided
      if (row.status && !Object.values(CLIENT_STATUSES).includes(row.status)) {
        validationErrors.push(`Row ${index + 1}: Invalid status "${row.status}". Valid values: ${Object.values(CLIENT_STATUSES).join(', ')}`);
      }
    });
    
    return validationErrors;
  };

  const processUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    setErrors([]);
    setSuccess(false);
    
    try {
      // Parse the full file
      Papa.parse(file, {
        header: true,
        complete: async (results) => {
          const data = results.data.filter(row => Object.keys(row).length > 1 && row.firstName); // Filter out empty rows
          const validationErrors = validateData(data);
          
          if (validationErrors.length > 0) {
            setErrors(validationErrors);
            setUploading(false);
            return;
          }
          
          // Format the client data for bulk upload
          const clientsData = data.map(row => ({
            firstName: row.firstName,
            lastName: row.lastName || '',
            company: row.company || '',
            mobile: row.mobile,
            alternateMobile: row.alternateMobile || '',
            email: row.email || '',
            address: row.address || '',
            status: row.status || 'Lead',
            remarks: row.remarks || '',
            tags: row.tags || '',  // Keep tags as is - no processing
            firstContact: row.firstContact || null,
            nextFollowUp: row.nextFollowUp || null
          }));
          
          // Set progress to 50% for parsing completion
          setUploadProgress(50);
          
          try {
            // Use the bulk upload API endpoint
            const result = await clientApi.bulkUpload(clientsData);
            
            setUploadStats({
              total: result.total,
              success: result.successful,
              failed: result.failed
            });
            
            if (result.errors && result.errors.length > 0) {
              setErrors(result.errors.map(err => `${err.client}: ${err.error}`));
            }
            
            setSuccess(true);
            
            if (result.successful > 0) {
              toast.success(`Successfully uploaded ${result.successful} clients`);
            }
            
            if (result.failed > 0) {
              toast.error(`Failed to upload ${result.failed} clients`);
            }
          } catch (error) {
            setErrors([`Upload failed: ${error.response?.data?.error || error.message}`]);
            
            if (error.response?.data?.results) {
              const results = error.response.data.results;
              setUploadStats({
                total: results.total,
                success: results.successful,
                failed: results.failed
              });
              
              if (results.errors && results.errors.length > 0) {
                setErrors(prev => [...prev, ...results.errors.map(err => `${err.client}: ${err.error}`)]);
              }
            }
          }
          
          // Complete the progress bar
          setUploadProgress(100);
          setUploading(false);
        },
        error: (error) => {
          setErrors([`Error parsing CSV: ${error.message}`]);
          setUploading(false);
        }
      });
    } catch (error) {
      setErrors([`Upload failed: ${error.message}`]);
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Use the pre-created template file from the public folder
    const link = document.createElement('a');
    link.setAttribute('href', '/client_template.csv');
    link.setAttribute('download', 'client_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Card.Title className="mb-4">Bulk Upload Clients</Card.Title>
        
        <div className="mb-4">
          <p className="text-muted">
            Upload a CSV file containing multiple client records. 
            <Button 
              variant="link" 
              className="p-0 ms-2 text-decoration-none" 
              onClick={downloadTemplate}
            >
              Download template
            </Button>
          </p>
        </div>
        
        <Form.Group controlId="formFile" className="mb-4">
          <div className="file-upload-container">
            <div className="file-upload-area">
              <input
                type="file"
                onChange={handleFileChange}
                accept=".csv"
                className="file-input"
                disabled={uploading}
              />
              <div className="file-upload-content">
                <FaUpload className="upload-icon" />
                <p className="mb-0">
                  {file ? file.name : 'Drag & drop your CSV file here or click to browse'}
                </p>
                {file && (
                  <span className="file-size">
                    {(file.size / 1024).toFixed(2)} KB
                  </span>
                )}
              </div>
            </div>
          </div>
        </Form.Group>
        
        {preview.length > 0 && (
          <div className="mb-4">
            <h6>Preview (first {preview.length} rows):</h6>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    {Object.keys(preview[0]).map((key) => (
                      <th key={key}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, index) => (
                    <tr key={index}>
                      {Object.values(row).map((value, i) => (
                        <td key={i}>{value}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
        
        {uploading && (
          <div className="mb-4">
            <ProgressBar 
              now={uploadProgress} 
              label={`${uploadProgress}%`} 
              animated 
              className="mb-2" 
            />
            <p className="text-center text-muted">Uploading clients... Please wait.</p>
          </div>
        )}
        
        {errors.length > 0 && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>Errors</Alert.Heading>
            <ul className="mb-0">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-4">
            <div className="d-flex align-items-center">
              <FaCheck className="me-2" />
              <div>
                <strong>Upload Complete!</strong>
                <p className="mb-0">
                  Total: {uploadStats.total} | 
                  Success: {uploadStats.success} | 
                  Failed: {uploadStats.failed}
                </p>
              </div>
            </div>
          </Alert>
        )}
        
        <div className="d-flex justify-content-end">
          <Button 
            variant="primary" 
            onClick={processUpload} 
            disabled={!file || uploading}
            className="d-flex align-items-center"
          >
            <FaFileAlt className="me-2" />
            Upload Clients
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default BulkUpload;
